import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { ScrapedProductRepository } from "@/repositories/scraped-product";
import { PaginatedResponse } from "@/types";
import { ScrapedProduct, UpdateScrapedProduct } from "@/types/scraped-product";

export const useUpdateScrapedProduct = () => {
  const queryClient = useQueryClient();
  const scrapedProductRepository = useRef(new ScrapedProductRepository());

  return useMutation({
    mutationFn: async (data: UpdateScrapedProduct) => {
      return await scrapedProductRepository.current.update(data);
    },
    onMutate: async (newData: UpdateScrapedProduct) => {
      const previousQueries = queryClient.getQueriesData({
        predicate: (query) =>
          query.queryKey[0] === "SCRAPED_PRODUCT" &&
          query.queryKey[1] === "LIST",
      });

      queryClient.setQueriesData(
        {
          predicate: (query) =>
            query.queryKey[0] === "SCRAPED_PRODUCT" &&
            query.queryKey[1] === "LIST",
        },
        (oldData) => {
          if (!oldData) return oldData;
          const cachedData = oldData as PaginatedResponse<ScrapedProduct>;

          return {
            ...cachedData,
            data: cachedData.data.map((product) =>
              product.id === newData.id
                ? {
                    ...product,
                    blacklist: newData.blacklist ?? product.blacklist,
                    reviewRequired:
                      newData.reviewRequired ?? product.reviewRequired,
                  }
                : product,
            ),
          };
        },
      );

      return { previousQueries };
    },
    onError: (_err, _newData, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "SCRAPED_PRODUCT" &&
          query.queryKey[1] === "LIST",
      });
    },
  });
};
